import React, { useCallback, useEffect, useMemo, useState } from "react"

import type { AxiosResponse } from "axios"
import { ErrorBoundary } from "react-error-boundary"

import { AuthenticationFields } from "./AuthenticationFields"
import { CustomTraySolutionFormContext } from "./CustomTraySolutionFormProvider"
import { FormProps, formRegistry } from "./forms"
import { Bee } from "~/src/components/BeeKit"
import { iClient } from "~/src/lib/appClients"
import { SolutionForSetup } from "~/src/types/solution"

export type CustomTraySolutionFormProps = {
  authValues?: string
  enabled: boolean
  initialData: string
  redirect?: string
  solution: SolutionForSetup
  solutionInstanceId: number
  storeId: number
}

export function CustomTraySolutionForm<TFormData extends object = Record<string, any>>({
  authValues,
  initialData,
  redirect,
  solution,
  solutionInstanceId,
  storeId,
}: CustomTraySolutionFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>()
  const [auths, setAuths] = useState<Record<string, string>>({
    ...solution.authentications.reduce((acc, auth) => ({ ...acc, [auth.externalId]: "" }), {}),
    ...(authValues == null ? {} : JSON.parse(authValues)),
  })
  const Form: React.JSXElementConstructor<FormProps<TFormData>> = useMemo(
    () => formRegistry[solution.solutionFormId],
    [solution.solutionFormId]
  )
  const initialParsedData: TFormData | undefined = useMemo(
    () => (initialData == null ? undefined : JSON.parse(initialData)),
    [initialData]
  )

  const fallbackRender = useCallback(
    ({ error }) => (
      <div>
        <h2 className="text-lg font-medium">Something went wrong</h2>
        <pre>{error.message}</pre>
      </div>
    ),
    []
  )

  const addErrors = useCallback(
    (errors: Record<string, string | string[]>) => {
      setErrors((prev) => {
        const newErrors = { ...prev }

        Object.keys(errors).forEach((key) => {
          const value = errors[key]
          if (newErrors[key] == null) {
            newErrors[key] = []
          }

          if (Array.isArray(value)) {
            newErrors[key] = [...newErrors[key], ...value]
          } else {
            newErrors[key] = [...newErrors[key], value]
          }
        })

        return newErrors
      })
    },
    [setErrors]
  )

  const handleSubmit = useCallback(
    async (formData: TFormData) => {
      let response: AxiosResponse
      setIsSubmitting(true)

      try {
        const solutionIdParam = encodeURIComponent(solution.id)
        const solutionInstanceIdParam = encodeURIComponent(solutionInstanceId)

        response = await iClient.patch(
          `/tray_io/solutions/${solutionIdParam}/instances/${solutionInstanceIdParam}`,
          { config: formData, auths },
          { params: { store_id: storeId } }
        )

        setErrors(undefined)
        window.location = redirect ?? response.headers.location
      } catch (error) {
        setErrors({
          __base: [error.message],
        })
        console.error(error)
      } finally {
        setIsSubmitting(false)
      }
    },
    [solution.id, solutionInstanceId, auths, storeId, redirect]
  )

  useEffect(() => {
    console.debug("Form: ", Form)
    console.debug("Solution: ", solution)
  }, [Form, solution])

  return (
    <ErrorBoundary fallbackRender={fallbackRender}>
      <CustomTraySolutionFormContext value={{ storeId }}>
        {errors?.__base && (
          <Bee.Flash kind="error">
            <ul>
              {errors.__base.map((error, i) => (
                <li key={i}>{error}</li>
              ))}
            </ul>
          </Bee.Flash>
        )}
        <AuthenticationFields
          authentications={solution.authentications}
          authValues={auths}
          solutionInstanceId={solutionInstanceId}
          onChange={(externalId, newValue) => {
            setAuths((prev) => ({ ...prev, [externalId]: newValue }))
          }}
        />
        <Form
          addValidationErrors={addErrors}
          errors={errors}
          initialData={initialParsedData}
          isSubmitting={isSubmitting}
          onCancel={() => {
            window.location.assign(redirect ?? "/")
          }}
          onSubmit={handleSubmit}
          title={solution.title}
        />
      </CustomTraySolutionFormContext>
    </ErrorBoundary>
  )
}
